// SCR-STAFF-608 — تنظیمات اسناد (US-DOC-002، 007): انواع سند سیستمی و تعریف‌شده مستأجر با حساسیت و کلاس نگهداری، سقف حجم بارگذاری، مدت اعتبار
// لینک اشتراک و مصرف فضای ذخیره‌سازی (تجمیع حداکثر هر ۵ دقیقه — AC-DOC-007.1).
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import { unwrap, type DocDocumentTypeView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, FormDialog, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { WriteNotice } from '../settings/common';
import { docKeys, fileSize, num, SCR_STAFF_608 } from './common';

const RETENTION = ['CLINICAL_STANDARD', 'CLINICAL_MINOR', 'FINANCIAL', 'OPERATIONAL_SHORT', 'MARKETING'];

function TypeDialog({ type, onClose }: { type: DocDocumentTypeView | null; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [code, setCode] = useState(type?.code ?? '');
  const [name, setName] = useState(type?.name ?? '');
  const [nameEn, setNameEn] = useState(type?.nameEn ?? '');
  const [sensitivity, setSensitivity] = useState<string>(type?.sensitivity ?? 'General');
  const [retention, setRetention] = useState(type?.retentionClass ?? 'CLINICAL_STANDARD');
  const [active, setActive] = useState(type?.isActive ?? true);
  const save = useMutation({
    mutationFn: () => {
      const body = { code, name, nameEn: nameEn || null, sensitivity: sensitivity as 'General', retentionClass: retention, isActive: active, rowVersion: type ? num(type.rowVersion) : null };
      return type
        ? unwrap(tenantApi.PUT('/api/v1/documents/document-types/{id}', { params: { path: { id: type.id } }, body }))
        : unwrap(tenantApi.POST('/api/v1/documents/document-types', { body }));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.types(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey={type ? 'doc:settings.type_edit' : 'doc:settings.type_create'} screenId={SCR_STAFF_608} onSubmit={() => save.mutate()} onClose={onClose} isDirty isSubmitting={save.isPending} submitError={save.error}>
      <TextField label={t('doc:builder.code')} value={code} onChange={(event) => setCode(event.target.value)} disabled={!!type} required slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'doc-type-code' } }} />
      <TextField label={t('doc:settings.name_fa')} value={name} onChange={(event) => setName(event.target.value)} disabled={type?.isSystem} required slotProps={{ htmlInput: { 'data-testid': 'doc-type-name' } }} />
      <TextField label={t('doc:settings.name_en')} value={nameEn} onChange={(event) => setNameEn(event.target.value)} disabled={type?.isSystem} slotProps={{ htmlInput: { dir: 'ltr' } }} />
      <TextField select label={t('doc:documents.columns.sensitivity')} value={sensitivity} onChange={(event) => setSensitivity(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'doc-type-sensitivity' } }}>
        {['General', 'Clinical', 'HighlySensitive'].map((value) => (
          <MenuItem key={value} value={value}>
            {t(`doc:sensitivity.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField select label={t('doc:settings.retention')} value={retention} onChange={(event) => setRetention(event.target.value)}>
        {RETENTION.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`doc:retention.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <FormControlLabel control={<Switch checked={active} onChange={(event) => setActive(event.target.checked)} />} label={t('doc:settings.active')} />
    </FormDialog>
  );
}

function LimitsPanel({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: docKeys.settings(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/settings')) });
  const [maxUpload, setMaxUpload] = useState('');
  const [shareHours, setShareHours] = useState('');
  useEffect(() => {
    if (settings.data) {
      setMaxUpload(String(settings.data.maxUploadMb));
      setShareHours(String(settings.data.shareHours));
    }
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () => unwrap(tenantApi.PUT('/api/v1/documents/settings', { body: { maxUploadMb: Number(maxUpload), shareHours: Number(shareHours), rowVersion: num(settings.data?.rowVersion) } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: docKeys.settings(session?.tenantId) }),
  });
  if (settings.error) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1">{t('doc:settings.limits')}</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField type="number" label={t('doc:settings.max_upload')} value={maxUpload} onChange={(event) => setMaxUpload(event.target.value)} disabled={!canWrite} slotProps={{ htmlInput: { 'data-testid': 'doc-settings-max-upload', min: 1, max: 100 } }} />
          <TextField type="number" label={t('doc:settings.share_hours')} value={shareHours} onChange={(event) => setShareHours(event.target.value)} disabled={!canWrite} slotProps={{ htmlInput: { min: 1, max: 72 } }} />
        </Stack>
        {settings.data ? (
          <Typography data-testid="doc-settings-storage">
            {t('doc:settings.storage', { size: fileSize(settings.data.storageBytes) })} · {t('doc:settings.scan', { state: settings.data.virusScanEnabled ? t('doc:settings.enabled') : t('doc:settings.disabled') })} ·{' '}
            {t('doc:settings.extensions', { list: settings.data.allowedExtensions.join(', ') })} · {t('doc:settings.max_video', { size: faDigits(num(settings.data.maxVideoMb)) })}
          </Typography>
        ) : null}
        {save.error ? <ErrorState error={save.error} compact /> : null}
        {save.isSuccess ? <Alert severity="success">{t('doc:settings.saved')}</Alert> : null}
        {canWrite ? (
          <Button variant="contained" sx={{ alignSelf: 'flex-start' }} onClick={() => save.mutate()} disabled={save.isPending} data-testid="doc-settings-save">
            {t('doc:actions.save')}
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );
}

export function DocumentSettingsPage() {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('doc.settings.manage');
  const [editing, setEditing] = useState<DocDocumentTypeView | null | 'new'>(null);
  const types = useQuery({ queryKey: docKeys.types(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/document-types')) });
  const columns: DataTableColumn<DocDocumentTypeView>[] = [
    { id: 'name', headerKey: 'doc:settings.columns.name', cell: (row) => row.name },
    { id: 'code', headerKey: 'doc:settings.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'sensitivity', headerKey: 'doc:documents.columns.sensitivity', cell: (row) => t(`doc:sensitivity.${row.sensitivity}`) },
    { id: 'retention', headerKey: 'doc:settings.retention', cell: (row) => t(`doc:retention.${row.retentionClass}`, { defaultValue: row.retentionClass }) },
    { id: 'kind', headerKey: 'doc:settings.columns.kind', cell: (row) => (row.isSystem ? t('doc:settings.system') : t('doc:settings.custom')) + (row.isActive ? '' : ` · ${t('doc:settings.inactive')}`) },
    {
      id: 'actions',
      headerKey: 'doc:builder.columns.actions',
      cell: (row) =>
        write.canWrite ? (
          <Button size="small" onClick={() => setEditing(row)} data-testid={`doc-type-edit-${row.code}`}>
            {t('doc:actions.edit')}
          </Button>
        ) : null,
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_608}
      titleKey="doc:settings.title"
      subtitleKey="doc:settings.subtitle"
      actions={write.canWrite ? <Button variant="contained" onClick={() => setEditing('new')} data-testid="doc-type-create">{t('doc:settings.type_create')}</Button> : undefined}
      filters={<WriteNotice write={write} />}
    >
      <LimitsPanel canWrite={write.canWrite} />
      <DataTable captionKey="doc:settings.types" columns={columns} rows={types.data?.items} getRowId={(row) => row.id} isLoading={types.isPending} error={types.error} onRetry={() => void types.refetch()} />
      {editing ? <TypeDialog type={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </ListPage>
  );
}
